import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useLayoutStore } from "~/store";
import { Heading, BtnBack, Icon, CarouselUi } from "~/components";
import { TopBanner } from "./TopBanner";
import { UpdateProfile } from "./UpdateProfile";
import { CardsLayout } from "./layouts";
import { ConfirmSaveBanner } from "./ConfirmSaveBanner";
import { useExplorePassions } from "./useExplorePassions";
import styles from "./ExplorePage.module.css";

import { data } from "./data";
import { getCookie } from "../../utils/getCookie";
import { setCookie } from "../../utils/setCookie";
import { useAcid } from "../../hooks/useAcid";
import { jtrCookieName } from "../../hooks/useSsoRedirect";
import { usePassions } from "../../hooks/usePassions";

export const ExplorePage = ({ showBanner = false, experienceType, view }) => {
  const { isResetting, setContentClassName } = useLayoutStore();
  const passions = useExplorePassions(experienceType);
  const cid = getCookie(jtrCookieName);
  const isCid = cid && Number(cid) !== 0;
  const { acid } = useAcid();
  const { savePassionsToServer } = usePassions(isCid ? cid : experienceType);

  useEffect(() => {
    if (!isCid || !acid) {
      return;
    }

    savePassionsToServer(acid, cid);
    setCookie(jtrCookieName, "", 0);
  }, [cid, acid]);

  useEffect(() => {
    if (isResetting) {
      return;
    }

    setContentClassName(styles.parentContent);
  }, [isResetting]);

  // Select Layout
  const [searchParams] = useSearchParams();
  const selectedView = useMemo(() => {
    const defaultView = <CardsLayout passions={passions} allPassions={data} />;
    const selected = view || Number(searchParams.get("view")) || 1;

    return (
      {
        1: defaultView,
        2: (
          <CarouselUi
            data-testid="explore-carousel"
            className={styles.carousel}
            passions={passions}
            allPassions={data}
          />
        ),
      }[selected] ?? defaultView
    );
  }, [data, passions, view, searchParams]);

  return (
    <>
      <BtnBack
        trackValue="Explore Header|Back to Experience"
        type="inversion"
        className={styles.back}
      >
        <Icon name="arrowRight" size="16" className={styles.backIcon} />
        Back
      </BtnBack>
      {showBanner && <TopBanner passions={passions} />}
      {cid && <ConfirmSaveBanner />}
      <Heading
        type="h1"
        text="Explore Your Passions"
        className={styles.heading}
      />
      <p className={styles.subheader}>
        Discover experiences to enrich your next trip with Marriott Bonvoy®.
      </p>
      <div className={styles.wrapper}>
        {selectedView}
        <UpdateProfile />
      </div>
    </>
  );
};
